/**
 * Catalogue des événements JET — dictionnaire **R19 v4.2 INTÉGRAL**.
 * Source unique : `reference/nf525/R19 - Version 4.2.xlsx`, onglet « Codes Evènements »
 * (colonnes A = Code, B = EXIGIBILITE/NF203, C = EXIGIBILITE/NF525, D = Intitulé du code /
 * Description, E = Purgeable, F = Commentaires). Le code numérique R19 est la valeur signée
 * (§2.3 `codeEvenement`).
 *
 * UTILITÉ : garde-fou de conformité. `jetEventByCode` REFUSE tout code hors dictionnaire, donc
 * ce catalogue est la liste close des événements que la caisse a le droit de sceller. Il sert
 * aussi de référence d'audit (exigibilité + purgeabilité) pour la purge partielle du JET (code 205).
 *
 * ⚠️ LIBELLÉS VERBATIM : `label` est la transcription MOT POUR MOT de la colonne D du xlsx, y
 * compris sa ponctuation, ses guillemets et ses doubles espaces d'origine (ex. code 126,
 * « Rajout  d’un… »). Ne JAMAIS reformuler un libellé ici — le commentaire de ligne porte l'usage,
 * le libellé porte la source.
 *
 * ⚠️ SEULE normalisation appliquée : suppression des espaces de DÉBUT/FIN de cellule (six libellés
 * du dictionnaire traînent une espace finale : codes 110, 123, 160, 190, 320, 430). AUCUNE autre
 * substitution de caractère n'est faite, et en particulier :
 *  · les espaces INSÉCABLES U+00A0 des libellés des codes 327 et 328 — le dictionnaire
 *    écrit «<U+00A0>offerte<U+00A0>», guillemets français collés par une insécable — sont
 *    CONSERVÉES. Elles sont écrites en séquence d'échappement `\u00a0` dans les littéraux :
 *    le caractère est invisible à la relecture, et un éditeur ou un copier-coller le remplacerait
 *    silencieusement par une espace ordinaire — ce qui ferait mentir la mention « verbatim » ;
 *  · les APOSTROPHES sont laissées telles quelles : le dictionnaire mélange lui-même la
 *    typographique U+2019 (code 60, « d’exercice ») et la droite U+0027 (code 95, « d'une
 *    rupture ») ; cette incohérence est celle de la source et ne doit JAMAIS être harmonisée ;
 *  · les points de suspension U+2026 (code 70) restent un caractère unique, non décomposé.
 * Contrôle : chaque `label` doit être égal, caractère pour caractère, à sa cellule D une fois
 * `trim()` appliqué.
 *
 * ⚠️ APPELS DE NOTE `[1]`/`[2]`/`[3]` — RÈGLE CÔTÉ CODE : **conservés** (ils appartiennent au
 * libellé source ; les retirer serait déjà une reformulation). Le tableau de lecture du SPEC les
 * retire par confort de lecture : c'est une VUE, et en cas de divergence c'est ce fichier qui fait
 * foi. Le contenu des trois notes est rappelé juste en dessous.
 *
 * ⚠️ DEUX COLONNES D'EXIGIBILITÉ, PAS DES CATÉGORIES : `exigence` = colonne **NF525** (celle qui
 * nous engage : Sliderz est un système d'encaissement) ; `exigenceNf203` = colonne **NF203**
 * (logiciels de comptabilité/gestion), conservée pour l'auditeur et parce qu'elle diverge parfois
 * (ex. code 180 : X en NF203, C en NF525 ; code 400 : C en NF203, O en NF525). Une colonne NF203
 * vide au dictionnaire = `null` (le code ne concerne pas ce référentiel).
 * Valeurs : 'X' = obligatoire · 'C' = conditionnel (obligatoire DÈS LORS que la fonction existe
 * dans le produit) · 'O' = optionnel.
 *
 * Notes de bas de tableau R19 (lignes 62-63 de l'onglet, reprises pour mémoire) :
 *  [1] Le code 10 servait à tracer les accès au JET avant R19 v4 ; un éditeur qui le conserve pour
 *      cet usage doit tracer le changement de séquence continue par le code 15 (ou un code ≥ 900).
 *  [2] L'applicabilité du code 329 peut évoluer (loi du 10 février 2020 anti-gaspillage).
 *  [3] Exemple pour le 323 : changement du prix — R19 déconseille de tracer les changements de
 *      quantité (volume d'événements).
 *
 * ⚠️ Codes éditeur « à partir de 900 » (O/O, purgeable) : R19 laisse l'éditeur définir ses propres
 * codes. Sliderz n'en déclare AUCUN — donc aucune entrée ici, et un code ≥ 900 est refusé au
 * scellement comme n'importe quel code inconnu.
 */

export interface JetEventDef {
  code: number;
  /** Libellé VERBATIM de la colonne D du dictionnaire R19 v4.2 (FR) */
  label: string;
  /** Exigibilité colonne NF525 — celle qui engage Sliderz */
  exigence: 'X' | 'C' | 'O';
  /** Exigibilité colonne NF203 (comptabilité/gestion) — `null` si la cellule est vide au dictionnaire */
  exigenceNf203: 'X' | 'C' | 'O' | null;
  /** Colonne « Purgeable » (O/N) : l'événement peut-il disparaître lors d'une purge partielle du JET (code 205) */
  purgeable: boolean;
}

/** Dictionnaire R19 v4.2 INTÉGRAL (onglet « Codes Evènements ») — indexé par code */
export const JET_CATALOG: readonly JetEventDef[] = [
  // ── Séquence continue & archivage ──────────────────────────────────────────
  // 10 et 15 partagent UNE ligne au dictionnaire (« 10 ou 15 ») : même libellé, même exigibilité.
  // À émettre quand les modalités de numérotation des pièces changent (évolution temporelle ou
  // organisationnelle), pas à chaque saut de numéro (ça, c'est le 95).
  { code: 10, label: 'Changement du mode de gestion de la séquence continue [1]', exigence: 'C', exigenceNf203: 'C', purgeable: false },
  { code: 15, label: 'Changement du mode de gestion de la séquence continue [1]', exigence: 'C', exigenceNf203: 'C', purgeable: false },
  // 20 purgeable SAUF si l'archive annuelle est un assemblage d'archives périodiques (R19 col. F) :
  // dans ce cas l'événement prouve la constitution de chaque morceau et doit survivre à la purge.
  { code: 20, label: 'Archivage fiscal de période', exigence: 'C', exigenceNf203: 'C', purgeable: true },
  { code: 30, label: "Archivage fiscal d'année ou d'exercice", exigence: 'X', exigenceNf203: 'X', purgeable: false },

  // ── Cycle de vie du terminal & des sessions ────────────────────────────────
  { code: 40, label: 'Arrêt du terminal, déconnexion, fermeture de session', exigence: 'C', exigenceNf203: null, purgeable: true },
  { code: 80, label: 'Démarrage du terminal, connexion, ouverture de session', exigence: 'C', exigenceNf203: null, purgeable: true },

  // ── Clôtures ───────────────────────────────────────────────────────────────
  { code: 50, label: 'Clôture de période (Jour/Mois)', exigence: 'X', exigenceNf203: 'X', purgeable: true },
  { code: 60, label: "Clôture annuelle ou d’exercice", exigence: 'X', exigenceNf203: 'X', purgeable: false },

  // ── Intégrité & continuité ─────────────────────────────────────────────────
  // 90 : à émettre dès qu'une donnée soumise à sécurisation s'avère modifiée a posteriori
  // (vérification de chaîne, contrôle d'archive) — c'est l'alerte fiscale la plus grave.
  { code: 90, label: 'Détection d’un défaut d’intégrité dans les données sécurisées ou dans une archive fiscale', exigence: 'X', exigenceNf203: 'X', purgeable: false },
  // 95 : trou dans la numérotation constaté À L'USAGE, hors changement de mode géré par le 10.
  { code: 95, label: "Détection d'une rupture de la séquence continue", exigence: 'C', exigenceNf203: 'C', purgeable: false },

  // ── Fonctions spéciales (modes école/formation) ────────────────────────────
  // ⚠️ Sliderz n'embarque AUCUNE fonction spéciale (ni mode école, ni mode formation) : ces deux
  // codes restent au catalogue pour la traçabilité du dictionnaire mais ne sont JAMAIS émis
  // (décision produit, SPEC retail §14bis.1). Les rétablir imposerait de les câbler.
  { code: 100, label: "Début de l'exécution d'une fonction spéciale", exigence: 'C', exigenceNf203: 'C', purgeable: false },
  { code: 105, label: "Fin de l'exécution d'une fonction spéciale", exigence: 'C', exigenceNf203: 'C', purgeable: false },

  // ── Flux de données ────────────────────────────────────────────────────────
  // 110 : tout export HORS comptabilité. ⚠️ CE QUE R19 DIT (col. Commentaires, verbatim) : « un export
  // de données peut être, par exemple, la production d'un fichier associé au Z de caisse, une
  // exportation vers un module de gestion du stock… », et il exclut explicitement l'export en
  // comptabilité, qui a son code dédié (180). ⚠️ CE QUE NOUS EN DÉDUISONS, et qui n'est PAS écrit au
  // dictionnaire : y ranger aussi une consultation/extraction par l'auditeur. La distinction compte —
  // la REMISE D'UNE ARCHIVE, elle, a son propre code (440), et c'est lui qu'on emploie.
  { code: 110, label: 'Exportation de données', exigence: 'C', exigenceNf203: 'C', purgeable: true },
  { code: 140, label: 'Importation de données externes', exigence: 'C', exigenceNf203: 'C', purgeable: true },

  // ── Mode dégradé (terminal déconnecté du système central) ──────────────────
  { code: 70, label: 'Début de mode dégradé (hors connexion, …)', exigence: 'C', exigenceNf203: null, purgeable: false },
  { code: 120, label: 'Fin de mode dégradé', exigence: 'C', exigenceNf203: null, purgeable: false },
  { code: 123, label: 'Renvoi forcé de données vers le système central', exigence: 'C', exigenceNf203: null, purgeable: false }, // ne peut survenir qu'APRÈS un 120
  { code: 125, label: 'Centralisation des données après la fin du mode dégradé', exigence: 'C', exigenceNf203: null, purgeable: false },

  // ── Parc, assujetti, droits ────────────────────────────────────────────────
  { code: 126, label: 'Rajout  d’un point de vente ou d’un poste utilisateur', exigence: 'O', exigenceNf203: null, purgeable: false },
  { code: 127, label: 'Suppression d’un point de vente ou d’un poste utilisateur', exigence: 'O', exigenceNf203: null, purgeable: false },
  // 128 : changement de SIREN détenteur. SEUL cas où les grands totaux perpétuels GTPCA/GTPVA
  // peuvent repartir — et uniquement après avoir MÉMORISÉ l'entité sortante et ses cumuls.
  { code: 128, label: "Changement d'assujetti utilisant le système d'encaissement", exigence: 'C', exigenceNf203: null, purgeable: false },
  // 130 : tout changement de droits/profil/groupe qui touche une caractéristique certifiée.
  { code: 130, label: 'Habilitation d’utilisateur/Gestion des droits', exigence: 'C', exigenceNf203: 'C', purgeable: true },

  // ── Impression & duplicata ─────────────────────────────────────────────────
  // 150 : si le logiciel ne sait pas détecter l'indisponibilité, l'éditeur doit le justifier (R19).
  { code: 150, label: 'Imprimante indisponible', exigence: 'C', exigenceNf203: null, purgeable: true },
  { code: 155, label: 'Duplicata d’une note', exigence: 'C', exigenceNf203: null, purgeable: true },
  { code: 156, label: 'Duplicata d’un justificatif de paiement', exigence: 'C', exigenceNf203: null, purgeable: true },

  // ── Comptabilité ───────────────────────────────────────────────────────────
  { code: 160, label: 'Enregistrement des écritures en comptabilité', exigence: 'O', exigenceNf203: 'O', purgeable: false }, // écriture DIRECTE dans la base comptable
  { code: 180, label: "Génération d'un fichier d'exportation des écritures comptables", exigence: 'C', exigenceNf203: 'X', purgeable: true },

  // ── Caisse & pièces justificatives ─────────────────────────────────────────
  { code: 170, label: 'Traitement de fond de caisse', exigence: 'C', exigenceNf203: null, purgeable: true },
  { code: 190, label: 'Annulation de pièce justificative ticket', exigence: 'C', exigenceNf203: null, purgeable: true }, // ne couvre PAS l'émission d'une facture d'avoir

  // ── Purge / reprise / sauvegarde ───────────────────────────────────────────
  { code: 200, label: 'Purge des données', exigence: 'C', exigenceNf203: 'C', purgeable: false }, // OBLIGATOIREMENT précédé d'une clôture ET d'un archivage
  { code: 205, label: 'Purge Partielle du JET', exigence: 'C', exigenceNf203: 'C', purgeable: false }, // ne peut viser que les entrées `purgeable: true`, après archive annuelle
  { code: 210, label: 'Reprise de données d’autres logiciels suite à changement de systèmes', exigence: 'C', exigenceNf203: 'C', purgeable: false },
  { code: 220, label: 'Restauration d’une base de données', exigence: 'C', exigenceNf203: 'C', purgeable: false }, // imposé dès qu'une fonction de restauration existe
  { code: 230, label: 'Sauvegarde d’une base de données', exigence: 'C', exigenceNf203: 'C', purgeable: false }, // imposé dès qu'une fonction de sauvegarde existe
  { code: 260, label: 'Initialisation des données', exigence: 'C', exigenceNf203: 'C', purgeable: false }, // typiquement la première mise en service

  // ── Maintenance & versions ─────────────────────────────────────────────────
  // 240 : code MANUEL. L'éditeur qui intervient en base (et risque de rompre le chaînage) doit le
  // saisir lui-même AVEC la raison de l'intervention.
  { code: 240, label: 'Suivi des interventions de maintenance', exigence: 'C', exigenceNf203: 'C', purgeable: false },
  { code: 250, label: 'Installation d’une nouvelle version majeure du logiciel', exigence: 'X', exigenceNf203: 'X', purgeable: false },
  { code: 255, label: 'Installation d’une nouvelle version mineure du logiciel', exigence: 'C', exigenceNf203: 'C', purgeable: true },

  // ── Paramétrage certifié ───────────────────────────────────────────────────
  { code: 270, label: 'Evolution de paramètre de conformité aux règles', exigence: 'C', exigenceNf203: 'C', purgeable: false },
  { code: 300, label: 'Activation de paramètre autorisé et documenté', exigence: 'C', exigenceNf203: 'C', purgeable: false }, // remplacé par le 270, conservé à titre historique

  // ── Administration fiscale & expert-comptable ──────────────────────────────
  // 280 : si un profil « Contrôle Fiscal » existe, sa PREMIÈRE connexion doit produire l'événement.
  { code: 280, label: 'Début/fin d’opération de contrôle fiscal', exigence: 'C', exigenceNf203: 'C', purgeable: true },
  { code: 290, label: 'Echange avec un expert-comptable (envoi du FEC ou du Z de caisse)', exigence: 'O', exigenceNf203: 'O', purgeable: true },
  { code: 295, label: "Transfert de fichier à la demande de l'administration", exigence: 'C', exigenceNf203: 'C', purgeable: false }, // cadre CCI, scellement éventuel inclus

  // ── Vie de la transaction (avant / après enregistrement) ───────────────────
  { code: 320, label: 'Abandon d’une opération de vente (transaction) commerciale non encore enregistrée', exigence: 'C', exigenceNf203: null, purgeable: true },
  // ⚠️ 323 : la cellule « Code » du dictionnaire contient littéralement « #323 » (marqueur éditorial
  // lié à l'appel de note [3], lui aussi préfixé « # » dans le bas de tableau). Le code retenu est
  // bien 323 — position entre 320 et 324 et note [3] applicable. À VÉRIFIER auprès d'AFNOR si un
  // organisme conteste ce « # ».
  { code: 323, label: 'Annulation/modification d’une ligne d’article [3]', exigence: 'C', exigenceNf203: null, purgeable: true },
  { code: 324, label: "Abandon d'une note validée", exigence: 'C', exigenceNf203: null, purgeable: true }, // « note » au sens restauration : note signée contre-passée
  { code: 325, label: 'Annulation(s) d’article(s) après commande', exigence: 'C', exigenceNf203: 'O', purgeable: true },
  { code: 326, label: "Retour(s) d'article(s) après une émission d’une note", exigence: 'C', exigenceNf203: null, purgeable: true },
  // 327/328 : les espaces autour des guillemets sont INSÉCABLES au dictionnaire — échappées ici
  // pour qu'elles survivent aux relectures (cf. en-tête, « SEULE normalisation appliquée »).
  { code: 327, label: 'Passage en note «\u00a0offerte\u00a0»', exigence: 'C', exigenceNf203: null, purgeable: true },
  { code: 328, label: 'Passage d’article en «\u00a0offert\u00a0»', exigence: 'C', exigenceNf203: 'O', purgeable: true },
  { code: 329, label: 'Non édition d’une note [2]', exigence: 'C', exigenceNf203: null, purgeable: true }, // trace les notes NON imprimées
  { code: 330, label: 'Initialisation de la clôture de fin de période', exigence: 'O', exigenceNf203: 'O', purgeable: true }, // début de traitement, quand la clôture se fait en plusieurs passes

  // ── Tranche 400-450 : exercice, assujetti, règlement, archives, clés ───────
  { code: 400, label: 'Changement de la durée de l’exercice', exigence: 'O', exigenceNf203: 'C', purgeable: false },
  // 410 : toute modification impactant l'identification de l'assujetti (adresse, SIRET,
  // dénomination sociale…) — donnée reportée sur les pièces justificatives, donc traçable.
  { code: 410, label: 'Changement de données affectant l’identification de l’assujetti ou du mandataire', exigence: 'C', exigenceNf203: 'C', purgeable: false },
  // 420 : code DÉDIÉ au changement de moyen de paiement APRÈS émission du ticket — à préférer à un
  // 190 « annulation » quand la pièce reste valide et que seul le règlement change.
  { code: 420, label: 'Changement du mode de règlement, après émission du ticket', exigence: 'C', exigenceNf203: null, purgeable: true },
  { code: 430, label: 'Changement de table (en restauration) affectée à une note', exigence: 'C', exigenceNf203: null, purgeable: true },
  { code: 440, label: 'Transfert des archives fiscales sur support externe', exigence: 'O', exigenceNf203: 'O', purgeable: false },
  // 450 : rotation du couple de clés de signature — sans cet événement, une chaîne signée par deux
  // clés successives est indistinguable d'une chaîne falsifiée.
  { code: 450, label: 'Traçabilité du changement du trousseau de clés utilisé pour signer', exigence: 'C', exigenceNf203: 'C', purgeable: false },
];

/** Alias nommés des événements émis par Sliderz (liste « à implémenter en priorité », §14.2) */
export const JET_EVENTS = {
  CLOTURE_PERIODE: 50, // Z jour/mois (§9.2)
  CLOTURE_ANNUELLE: 60, // clôture exercice (§9.4.A)
  ARCHIVAGE_PERIODE: 20,
  ARCHIVAGE_ANNUEL: 30,
  OUVERTURE_SESSION: 80, // login (§3)
  FERMETURE_SESSION: 40, // logout (§3)
  DEBUT_MODE_DEGRADE: 70, // passage hors ligne (§13)
  FIN_MODE_DEGRADE: 120, // retour en ligne
  CENTRALISATION_APRES_DEGRADE: 125, // sync différée (§13)
  DUPLICATA_NOTE: 155, // duplicata (§8)
  DUPLICATA_JUSTIFICATIF: 156,
  FOND_DE_CAISSE: 170, // ouverture/dépôt/prélèvement (§9/§10)
  ANNULATION_PIECE: 190, // annulation (§7/§8)
  ABANDON_VENTE: 320, // vider le ticket avant validation (§4.7)
  REGLAGE_CERTIFIE: 270, // changement réglage certifié (§12.9)
  VERSION_MAJEURE: 250,
  VERSION_MINEURE: 255,
  DEFAUT_INTEGRITE: 90, // déclenché par l'Inspecteur (§14.6)
  RUPTURE_SEQUENCE: 95,
  EXPORT_DONNEES: 110, // exports (dont accès Inspecteur NF525)
  EXPORT_COMPTABLE: 180,
  CHANGEMENT_ASSUJETTI: 128, // seul cas de RAZ GTPCA/GTPVA autorisé (tracé)
  SAUVEGARDE_BASE: 230,
  RESTAURATION_BASE: 220,
  // Codes de la tranche 400 ouverts par la complétion du catalogue (2026-07-22) : ils EXISTENT au
  // dictionnaire et remplacent des approximations faites faute de code connu.
  CHANGEMENT_REGLEMENT: 420, // remplace l'usage détourné du 190 pour un changement de règlement post-ticket
  CHANGEMENT_IDENTITE_ASSUJETTI: 410, // édition raison sociale / SIRET / adresse (§12)
  CHANGEMENT_TROUSSEAU_CLES: 450, // rotation de la clé de signature Keystore
  INITIALISATION_DONNEES: 260, // première mise en service de la base
  /**
   * 440 — l'archive fiscale SORT de notre garde (remise au commerçant, dépôt sur support externe).
   * ⚠️ NON PURGEABLE au dictionnaire, et c'est cohérent : la trace d'une archive qui est partie doit
   * survivre à la purge du journal qu'un archivage annuel autorise.
   * ⚠️ À NE PAS CONFONDRE AVEC LE 110 : R19 réserve celui-ci à l'export de DONNÉES (fichier joint au
   * Z, alimentation d'un module de stock) et ne parle jamais d'archives.
   */
  TRANSFERT_ARCHIVES: 440,
} as const;

export type JetEventKey = keyof typeof JET_EVENTS;

const BY_CODE = new Map<number, JetEventDef>(JET_CATALOG.map(e => [e.code, e]));

/** Résout un code R19 → définition (throw si code hors catalogue : on ne signe pas d'inconnu) */
export function jetEventByCode(code: number): JetEventDef {
  const def = BY_CODE.get(code);
  if (!def) throw new Error(`core-nf525: code événement JET inconnu (${code}) — hors dictionnaire R19 v4.2`);
  return def;
}
